import { Loader2, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { withCacheBust } from "@/lib/utils";
import type { Scenario } from "@/lib/powtoon-be/types";

interface Props {
  scenario: Scenario;
  subtitle?: string;
  onRestart: () => void;
  /**
   * The session is still wrapping up (teardown / transcript / scoring in
   * flight). Keeps the screen visible but blocks restarting until it's done.
   */
  busy?: boolean;
}

/**
 * Shared post-session ("ended") screen for all providers. Provider-agnostic:
 * reads `scenario.thumb_url` (populated for both LiveAvatar and Tavus) and
 * calls back with `onRestart`. The pre/post session UI is owned by the
 * platform — only the live runtime differs between providers.
 */
export function PostSessionScreen({ scenario, subtitle, onRestart, busy = false }: Props) {
  const thumb = withCacheBust(scenario.thumb_url, scenario.modified);
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background px-6">
      <div className="flex max-w-sm flex-col items-center text-center space-y-6">
        <div className="h-28 w-28 rounded-3xl bg-accent border border-border overflow-hidden">
          {thumb && <img src={thumb} className="h-full w-full object-cover" alt="" />}
        </div>
        <div>
          <p className="text-lg font-semibold text-foreground">{scenario.name}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {busy ? "Wrapping up…" : (subtitle ?? "Session ended")}
          </p>
        </div>
        <Button size="lg" className="w-full max-w-xs" onClick={onRestart} disabled={busy}>
          {busy ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <PlayCircle className="h-5 w-5" />
          )}
          Restart session
        </Button>
      </div>
    </div>
  );
}